// ── offlineStorage.js ─────────────────────────────────────────────────────
// Manages tasks + subtasks for guest/offline users in localStorage.

const KEY = 'dayrit_offline_tasks';

// Local YYYY-MM-DD — toISOString() is UTC which shifts date in UTC+ timezones
function localDateStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
}

function load() {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
  catch { return []; }
}

function save(tasks) {
  try { localStorage.setItem(KEY, JSON.stringify(tasks)); }
  catch (e) { console.error('offlineStorage.save failed:', e); }
}

function offlineId() {
  return `offline_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

export const offlineStorage = {
  // ── Tasks ───────────────────────────────────────────────────────────────
  getAll() { return load(); },

  add(task) {
    const tasks = load();
    const newTask = {
      ...task,
      id               : offlineId(),
      done             : false,
      subtasks         : [],
      recurrence_type  : task.recurrence_type   || null,
      recurrence_config: task.recurrence_config || null,
      created_at       : new Date().toISOString(),
      _offline         : true,
    };
    tasks.unshift(newTask);
    save(tasks);
    return newTask;
  },

  update(id, changes) {
    const tasks = load();
    const idx = tasks.findIndex(t => t.id === id);
    if (idx !== -1) {
      tasks[idx] = { ...tasks[idx], ...changes };
      save(tasks);
      return tasks[idx];
    }
    return null;
  },

  toggle(id) {
    const tasks = load();
    const idx = tasks.findIndex(t => t.id === id);
    if (idx !== -1) {
      tasks[idx].done = !tasks[idx].done;
      tasks[idx].completed_at = tasks[idx].done ? new Date().toISOString() : null;
      save(tasks);
      return tasks[idx];
    }
    return null;
  },

  remove(id) {
    save(load().filter(t => t.id !== id));
  },

  clear() {
    localStorage.removeItem(KEY);
  },

  // Count tasks (for offline badge)
  count() { return load().length; },

  // ── Subtasks ─────────────────────────────────────────────────────────────
  addSubtask(taskId, title) {
    const tasks = load();
    const idx = tasks.findIndex(t => t.id === taskId);
    if (idx === -1) return null;

    const subtask = {
      id          : offlineId(),
      task_id     : taskId,
      title       : title.trim(),
      done        : false,
      sort_order  : (tasks[idx].subtasks || []).length,
      completed_at: null,
      _offline    : true,
    };

    tasks[idx].subtasks = [...(tasks[idx].subtasks || []), subtask];
    save(tasks);
    return subtask;
  },

  updateSubtask(taskId, subtaskId, changes) {
    const tasks = load();
    const tIdx = tasks.findIndex(t => t.id === taskId);
    if (tIdx === -1) return null;

    const subs = tasks[tIdx].subtasks || [];
    const sIdx = subs.findIndex(s => s.id === subtaskId);
    if (sIdx === -1) return null;

    subs[sIdx] = { ...subs[sIdx], ...changes };
    tasks[tIdx].subtasks = subs;
    save(tasks);
    return subs[sIdx];
  },

  toggleSubtask(taskId, subtaskId) {
    const tasks = load();
    const tIdx = tasks.findIndex(t => t.id === taskId);
    if (tIdx === -1) return null;

    const subs = tasks[tIdx].subtasks || [];
    const sIdx = subs.findIndex(s => s.id === subtaskId);
    if (sIdx === -1) return null;

    subs[sIdx].done = !subs[sIdx].done;
    subs[sIdx].completed_at = subs[sIdx].done ? new Date().toISOString() : null;
    tasks[tIdx].subtasks = subs;
    save(tasks);
    return subs[sIdx];
  },

  removeSubtask(taskId, subtaskId) {
    const tasks = load();
    const idx = tasks.findIndex(t => t.id === taskId);
    if (idx !== -1) {
      tasks[idx].subtasks = (tasks[idx].subtasks || []).filter(s => s.id !== subtaskId);
      save(tasks);
    }
  },
};

// ── Habits (offline / guest) ───────────────────────────────────────────────
const HABITS_KEY = 'dayrit_offline_habits';

function loadHabits() {
  try { return JSON.parse(localStorage.getItem(HABITS_KEY) || '[]'); }
  catch { return []; }
}

function saveHabits(habits) {
  try { localStorage.setItem(HABITS_KEY, JSON.stringify(habits)); }
  catch (e) { console.error('habitOfflineStorage.save failed:', e); }
}

function buildWeek(habit) {
  // Current week Mon→Sun (matches backend startOfWeek(MONDAY))
  const today = new Date(); today.setHours(0, 0, 0, 0);
  // Find Monday of current week (day 1). Sunday=0, so (day+6)%7 days back
  const dayOfWeek = today.getDay(); // 0=Sun,1=Mon,...,6=Sat
  const daysFromMon = (dayOfWeek + 6) % 7; // Mon=0, Tue=1, ..., Sun=6
  const monday = new Date(today);
  monday.setDate(today.getDate() - daysFromMon);

  const result = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    const dateStr   = localDateStr(d);
    const weekday   = d.getDay();
    const scheduled = (habit.target_days || [0,1,2,3,4,5,6]).includes(weekday);
    const done      = scheduled && (habit.completions || []).includes(dateStr);
    result.push({ date: dateStr, scheduled, done, weekday });
  }
  return result;
}

function computeStreak(habit) {
  const done       = new Set(habit.completions || []);
  const targetDays = habit.target_days || [0,1,2,3,4,5,6];
  const today      = new Date(); today.setHours(0,0,0,0);
  let streak = 0;
  let cursor = new Date(today);

  const todayStr = localDateStr(cursor);
  if (!done.has(todayStr) && targetDays.includes(cursor.getDay())) {
    cursor.setDate(cursor.getDate() - 1);
  }

  for (let i = 0; i < 365; i++) {
    if (!targetDays.includes(cursor.getDay())) { cursor.setDate(cursor.getDate() - 1); continue; }
    const str = localDateStr(cursor);
    if (!done.has(str)) break;
    streak++;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

function formatHabitResponse(habit) {
  return { ...habit, week: buildWeek(habit), streak: computeStreak(habit) };
}

export const habitOfflineStorage = {
  getAll() { return loadHabits().map(formatHabitResponse); },

  // Count habits (for offline badge)
  count() { return loadHabits().length; },

  add(data) {
    const habits = loadHabits();
    const habit = {
      id          : `offline_${Date.now()}_${Math.random().toString(36).slice(2,7)}`,
      name        : data.name,
      emoji       : data.emoji       || '⭐',
      color       : data.color       || 'var(--accent)',
      frequency   : data.frequency   || 'daily',
      target_days : data.target_days || [0,1,2,3,4,5,6],
      archived    : false,
      sort_order  : habits.length,
      completions : [],
      created_at  : new Date().toISOString(),
      _offline    : true,
    };
    habits.push(habit);
    saveHabits(habits);
    return formatHabitResponse(habit);
  },

  update(id, changes) {
    const habits = loadHabits();
    const idx = habits.findIndex(h => h.id === id);
    if (idx === -1) return null;
    habits[idx] = { ...habits[idx], ...changes };
    saveHabits(habits);
    return formatHabitResponse(habits[idx]);
  },

  remove(id) { saveHabits(loadHabits().filter(h => h.id !== id)); },

  toggle(id, dateStr) {
    const today = new Date(); today.setHours(0,0,0,0);
    const date  = new Date(dateStr + 'T00:00:00');
    if (date > today) return null;

    const habits = loadHabits();
    const idx = habits.findIndex(h => h.id === id);
    if (idx === -1) return null;

    const completions = habits[idx].completions || [];
    const pos = completions.indexOf(dateStr);
    habits[idx].completions = pos >= 0
      ? completions.filter(d => d !== dateStr)
      : [...completions, dateStr].sort();

    saveHabits(habits);
    return formatHabitResponse(habits[idx]);
  },
};